import type { TradeItem, MoneySummary } from '@/types';
import { formatCurrency } from './mtm-calculations';
import { aggregateByCurrency, aggregateFromBuckets, type CurrencyAggregate } from './currency-totals';
import { getEffectiveSettlementCurrency } from './settlement-ccy';
import { SUMMABLE_BUCKET_CCY, SUMMABLE_COLUMNS } from './csv-totals';

/**
 * One column's total for the on-page bottom totals footer (LR-469).
 *
 * The footer is a compact, one-line-per-column summary, so unlike the CSV export
 * (which lists every per-currency subtotal on its own row) a single cell can only
 * show ONE figure. Every summable column is a row-currency amount (see
 * SUMMABLE_COLUMNS), so the rule is:
 *  - a single-currency column shows its NATIVE total (no conversion noise);
 *  - a multi-currency column shows the `≈ base` roll-up (the only honest single
 *    number) and exposes the per-currency breakdown via `title`.
 */
export interface FooterColumnTotal {
  /** Display text for the cell, e.g. `$1.2M` (native) or `≈ €3.4M` (rolled up). */
  text: string;
  /** Tooltip: the full per-currency breakdown (+ the `≈ base` line when rolled up). */
  title: string;
  /** True when the displayed figure relied on a stale/fallback FX rate AND that
   *  rate's share of the roll-up is material (render `⚠`). */
  approx: boolean;
  /** The non-live currencies behind `approx` (for a specific ⚠ tooltip). Empty
   *  when `approx` is false. */
  approxCurrencies: string[];
  /** Numeric counterpart of `text` (native sum or ≈ equivalent) — for sign-based styling. */
  value: number;
  /** Display base of the `≈` roll-up (e.g. 'EUR'). For the structured breakdown tooltip. */
  base: string;
  /** Roll-up of every bucket converted into `base` (equals the native sum when single-ccy). */
  equiv: number;
  /** Currencies present, base-first then alphabetical — drives the breakdown tooltip rows. */
  currencies: string[];
  /** Native per-currency subtotals — the breakdown tooltip rows. */
  byCcy: Record<string, number>;
}

/**
 * The ⚠ marker is suppressed when the non-live currencies make up less than this
 * share of the gross roll-up. A trade book often carries a tiny tail in a
 * currency the live feed doesn't publish (e.g. a few thousand RUB among tens of
 * millions of EUR/USD) — flagging the whole multi-million total as "approximate"
 * over a sub-1% sliver trained users to ignore the marker. The per-currency
 * breakdown stays in the tooltip regardless, so nothing is hidden — only the
 * loud marker is gated. Raise/lower as a product call.
 */
export const APPROX_MATERIAL_SHARE = 0.01;

/** Human note for the ⚠ marker — names the offending currencies when known. */
export const approxRateNote = (ccys: string[]): string =>
  ccys.length === 0
    ? 'Some rates are approximate (live FX unavailable)'
    : `Approximate — no live FX for ${ccys.join(', ')}; rolled up at a fallback rate`;

/** Full-precision `1,234,567.89 CCY` for the tooltip breakdown (the cell stays compact). */
export const formatExactAmount = (value: number, ccy: string): string =>
  `${new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value)} ${ccy}`;

/**
 * The display rule shared by the totals footer and the MetricsBar money cells
 * (kvi #4): a single-currency aggregate shows its NATIVE total (no conversion
 * noise); a multi-currency one shows the `≈ base` roll-up — the only honest
 * single number — with the per-currency breakdown in `title`.
 *
 * An EMPTY aggregate (no finite contribution at all) is the CALLER's decision, not this
 * function's: it renders a base-denominated zero, which is what the metric cells want (a
 * fixed grid of figures must not grow holes — `MtmAggregateBar`, `use-view-metrics`), and
 * what a column of "—" cells must never sit under (the footers and the CSV totals block
 * therefore skip the column instead of calling in, see `buildFooterColumnTotals`). Base-zero
 * is the fallback, not the contract.
 *
 * The structured `base`/`equiv`/`currencies`/`byCcy` are carried through verbatim
 * from `agg` so a richer consumer (the MoneyMetricCell hover card) can render the
 * breakdown as a list instead of parsing `title`.
 */
export const describeCurrencyTotal = (agg: CurrencyAggregate): FooterColumnTotal => {
  const ccys = agg.currencies;
  const structured = { base: agg.base, equiv: agg.equiv, currencies: ccys, byCcy: agg.byCcy };
  if (ccys.length === 0) {
    return { text: formatCurrency(0, agg.base), title: '', approx: false, approxCurrencies: [], value: 0, ...structured };
  }
  if (ccys.length === 1) {
    const ccy = ccys[0];
    const value = agg.byCcy[ccy];
    return { text: formatCurrency(value, ccy), title: formatExactAmount(value, ccy), approx: false, approxCurrencies: [], value, ...structured };
  }
  const breakdown = ccys.map((c) => formatExactAmount(agg.byCcy[c], c)).join('\n');
  // ⚠ only when a fallback rate was used AND its share of the roll-up is material
  // (a sub-threshold tail in an un-fed currency doesn't warrant alarming the whole
  // multi-currency total — see APPROX_MATERIAL_SHARE).
  const approx = agg.usedFallback && agg.approxShare >= APPROX_MATERIAL_SHARE;
  return {
    text: `≈ ${formatCurrency(agg.equiv, agg.base)}`,
    title: `${breakdown}\n≈ ${formatExactAmount(agg.equiv, agg.base)}`,
    approx,
    approxCurrencies: approx ? agg.approxCurrencies : [],
    value: agg.equiv,
    ...structured,
  };
};

/**
 * Build the per-column totals shown in the sticky bottom footer (LR-469), keyed by
 * column id. Only the SUMMABLE_COLUMNS that are actually visible get an entry — a
 * column with no entry renders blank in the footer. Totals the SAME columns by the
 * SAME currency basis as the CSV export (`SUMMABLE_COLUMNS`), reusing the same
 * `aggregateByCurrency` primitive, so the footer and a CSV of the same view agree.
 *
 * @param rows    the rows to total (callers pre-scope: Trades = all visible rows;
 *                MTM = eligible & not-excluded rows).
 * @param visibleColumnIds visible columns in display order; non-summable ids are skipped.
 * @param base    the display base for the multi-currency `≈` roll-up (EUR/USD toggle).
 */
export function buildFooterColumnTotals(
  rows: TradeItem[],
  visibleColumnIds: string[],
  base = 'EUR',
): Record<string, FooterColumnTotal> {
  const baseUpper = base.toUpperCase();
  const out: Record<string, FooterColumnTotal> = {};
  if (rows.length === 0) return out;

  for (const id of visibleColumnIds) {
    const get = SUMMABLE_COLUMNS[id];
    if (!get) continue;
    // Per SETTLEMENT currency (LR-474 D2), rolled up only when >1 ccy present.
    // Each column pairs its settlement-denominated accessor with its honest
    // bucket key (SUMMABLE_BUCKET_CCY) — key and amounts move together.
    const agg = aggregateByCurrency(
      rows,
      get,
      baseUpper,
      SUMMABLE_BUCKET_CCY[id] ?? getEffectiveSettlementCurrency,
    );
    // Not one row carried a figure → NO entry, so the cell renders blank like the "—"
    // cells above it. `describeCurrencyTotal` would otherwise print a base-currency
    // 0.00, i.e. "marked value is zero" on a book that was never marked. Same contract
    // as the CSV totals block and the MTM synthetic totals.
    if (agg.currencies.length === 0) continue;
    out[id] = describeCurrencyTotal(agg);
  }

  return out;
}

/**
 * Build the footer's per-column totals from the server's per-column, per-ccy
 * OPEN-book subtotal maps (`GET /positions/metrics/` `column_totals`). LR-546: the
 * windowed blotter footer reads these WHOLE-BOOK subtotals instead of totalling the
 * loaded page (`buildFooterColumnTotals`, kept for the MTM table which loads its
 * whole CP book). Each column's native per-ccy map is rolled up to `base` with LIVE
 * FX through the SAME `describeCurrencyTotal` path as the metrics bar, so a
 * single-ccy column shows its native total and a multi-ccy one the `≈ base` roll-up.
 *
 * A column the server can't total whole-book (rate-engine / COB) is simply ABSENT from
 * `columnTotals` → no entry → the footer renders "—" (CLIENT_ONLY_FOOTER_COLUMN_IDS)
 * rather than a misleading window figure.
 *
 * An EMPTY map (`{}`) is ambiguous on its own, because metrics.py builds a bucket for every
 * FOOTER_COLUMN_ID and then skips null amounts — so it means either "the open book is empty"
 * or "the open book has rows, but none of them carries this figure". The payload settles it,
 * and the two cases must render differently (kvi r3 #2):
 *  - EVERY column empty ⇒ the open book itself is empty ⇒ a base-currency `0` is a true
 *    statement about it, and that zero is what keeps LR-546's anti-vanish footer informative
 *    instead of a bare "Total" label;
 *  - this column empty while a sibling carries data ⇒ nothing has recorded this figure yet
 *    (never marked, no collateral) ⇒ NO entry, blank cell. A `0` there would read as "the
 *    marked value is zero" over a column of "—" cells — the same defect the client-side
 *    totals fixed, reached through the server path.
 */
export function buildFooterTotalsFromServer(
  columnTotals: Record<string, MoneySummary> | undefined,
  visibleColumnIds: string[],
  base = 'EUR',
): Record<string, FooterColumnTotal> {
  const out: Record<string, FooterColumnTotal> = {};
  if (!columnTotals) return out;
  const baseUpper = base.toUpperCase();
  // Whole-payload signal, not per column: does ANY column carry a figure? See the note above —
  // this is what separates "the open book is empty" from "nothing recorded this figure".
  const openBookHasFigures = Object.values(columnTotals).some(
    (m) => m && Object.keys(m).length > 0,
  );
  for (const id of visibleColumnIds) {
    const map = columnTotals[id];
    if (!map) continue;
    const numeric: Record<string, number> = {};
    for (const [ccy, v] of Object.entries(map)) {
      const n = parseFloat(v);
      if (Number.isFinite(n)) numeric[ccy] = n;
    }
    if (Object.keys(numeric).length === 0 && openBookHasFigures) continue;
    out[id] = describeCurrencyTotal(aggregateFromBuckets(numeric, baseUpper));
  }
  return out;
}
